import { Navigate, Outlet, useLocation } from "react-router-dom";
import GluceraNavigationRoutes from "../utils/navigationRoutes";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AUTH_SESSION_UPDATED,
  ensureFreshAccessToken,
  hasAuthSession,
  shouldLogoutAfterRefreshFailure,
} from "@/services/tokenRefresh";
import { clearGluceraAuthCookies } from "../utils/gluceraAuthCookies";
import assessmentStatusService from "../services/AssessmentStatusService";
import { StatusCache } from "../utils/statusCache";
import { ensureUserSessionExists } from "../utils/authSessionInvalidation";
import {
  isAssessmentFlowPath,
  isAssessmentInProgress,
  requiresCompletedAssessment,
} from "../utils/assessmentFlowPaths";

const SESSION_BUFFER_MS = 5 * 60 * 1000;

const GluceraProtectedRoute = () => {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const [authState, setAuthState] = useState<
    "loading" | "authenticated" | "unauthenticated"
  >("loading");
  const [isAssessmentComplete, setIsAssessmentComplete] = useState<boolean | null>(null);

  const checkAndRefreshToken = useCallback(async (forceRefreshAssessment = false) => {
    if (!hasAuthSession()) {
      setAuthState("unauthenticated");
      return;
    }

    const sessionOk = await ensureFreshAccessToken(SESSION_BUFFER_MS);
    if (sessionOk) {
      // During assessment the profile may not exist yet — skip deleted-user probe.
      if (!isAssessmentInProgress()) {
        const userExists = await ensureUserSessionExists();
        if (!userExists) return;
      }

      window.dispatchEvent(new CustomEvent(AUTH_SESSION_UPDATED));

      // Assessment completion is a one-time gate. Once we've confirmed it's
      // complete (flag persisted in localStorage), never call the API again —
      // returning users go straight to home. The API key is derived from the
      // access token, which rotates every few minutes, so without this
      // short-circuit the check re-fired on every mount / tab switch / refresh.
      if (
        !forceRefreshAssessment &&
        localStorage.getItem("glucera_onboarding_complete") === "true"
      ) {
        setIsAssessmentComplete(true);
        setAuthState("authenticated");
        return;
      }

      try {
        const token = localStorage.getItem("glucera_access_token");
        if (token) {
          const res = await assessmentStatusService.checkAssessmentCompletion(token, {
            forceRefresh: forceRefreshAssessment,
          });
          setIsAssessmentComplete(res.isComplete);
          if (res.isComplete) {
            localStorage.setItem("glucera_onboarding_complete", "true");
          }
        } else {
          setIsAssessmentComplete(false);
        }
      } catch {
        const locallyComplete =
          localStorage.getItem("glucera_onboarding_complete") === "true";
        setIsAssessmentComplete(locallyComplete);
      }

      setAuthState("authenticated");
      return;
    }

    // Refresh did not produce a fresh token. Only log the user out when the
    // server explicitly rejected the session (invalid/expired refresh token).
    // For transient failures (offline, slow network, 5xx, timeout) we keep the
    // user signed in and let the next refresh attempt recover the session.
    if (shouldLogoutAfterRefreshFailure()) {
      clearAllStorage();
      setAuthState("unauthenticated");
      return;
    }

    // Transient failure but we still hold a session — stay in the app.
    // Decide the onboarding gate without a fresh API result:
    //  - flag says complete            → home
    //  - we already knew the answer     → keep it
    //  - genuinely unknown (rare)       → don't block; never force a returning
    //                                     user back through onboarding over a
    //                                     temporary network/server hiccup.
    const locallyComplete =
      localStorage.getItem("glucera_onboarding_complete") === "true";
    setIsAssessmentComplete(prev =>
      locallyComplete ? true : prev !== null ? prev : false
    );
    setAuthState("authenticated");
  }, []);

  useEffect(() => {
    void checkAndRefreshToken();

    const onSessionUpdated = () => {
      setAuthState("authenticated");
    };
    window.addEventListener(AUTH_SESSION_UPDATED, onSessionUpdated);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Mobile fires visibility often — avoid re-gating mid-assessment.
        if (
          isAssessmentInProgress() &&
          isAssessmentFlowPath(pathnameRef.current)
        ) {
          return;
        }
        void checkAndRefreshToken();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener(AUTH_SESSION_UPDATED, onSessionUpdated);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [checkAndRefreshToken]);

  if (authState === "loading" || (authState === "authenticated" && isAssessmentComplete === null)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (authState === "authenticated") {
    const locallyComplete =
      localStorage.getItem("glucera_onboarding_complete") === "true";
    const completed = isAssessmentComplete || locallyComplete;

    // Block only main-app routes until assessment is done. Assessment screens
    // (including any new step) are allowed by default.
    if (!completed && requiresCompletedAssessment(location.pathname)) {
      return <Navigate to="/glucera/genderpage" replace />;
    }

    return <Outlet />;
  }

  return (
    <Navigate
      to={
        "/glucera" +
        GluceraNavigationRoutes.ONBOARDING_LOGINPAGE +
        location.search
      }
      replace
    />
  );
};

function clearAllStorage() {
  localStorage.removeItem("glucera_access_token");
  localStorage.removeItem("glucera_user_info");
  localStorage.removeItem("glucera_refresh_token");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userInfo");
  localStorage.removeItem("refreshToken");
  // Reset the onboarding gate so the next user on this device is re-checked.
  localStorage.removeItem("glucera_onboarding_complete");
  sessionStorage.clear();
  clearGluceraAuthCookies();
}

export default GluceraProtectedRoute;
